import { Container } from "layouts";
import useFetchSubscriptions from "api/SubscriptionsResource/useFetchSubscriptions";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import PaddingX from "layouts/PaddingX";
import useCurrencyStore from "store/currencyStore";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import { Link } from "react-router-dom";
import { activeCurrencies, getMoneyPrefix } from "utils";
import SubscriptionsPageLoader from "./SubscriptionsPageLoader";
import { motion } from "framer-motion";
import { useState } from "react";
import Moment from "react-moment";
import { FiChevronUp, FiBook, FiGrid, FiCreditCard } from "react-icons/fi";

// Animation variants for staggered children
const containerVariants = {
	hidden: { opacity: 0 },
	visible: {
		opacity: 1,
		transition: { staggerChildren: 0.08, delayChildren: 0.1 },
	},
};

const itemVariants = {
	hidden: { opacity: 0, y: 16 },
	visible: {
		opacity: 1,
		y: 0,
		transition: { type: "spring", stiffness: 400, damping: 28 },
	},
};

const statVariants = {
	hidden: { opacity: 0, scale: 0.9 },
	visible: {
		opacity: 1,
		scale: 1,
		transition: { type: "spring", stiffness: 300, damping: 24 },
	},
};

// Active Subscription View Component
const ActiveSubscriptionView = ({ subscription, onViewPlans }) => {
	const today = new Date();
	const isInstitutional = subscription?.category?.toLowerCase() === "institution";

	// Calculate usage
	const totalPicks = subscription?.max_number_of_books || 0;
	const usedPicks = subscription?.current_premium_books_added || 0;
	const remainingPicks = totalPicks - usedPicks;
	const usagePercentage = totalPicks > 0 ? (usedPicks / totalPicks) * 100 : 0;

	// Stats data for mapping
	const stats = [
		{ value: totalPicks, label: "Total Picks", highlight: false },
		{ value: usedPicks, label: "Used", highlight: true },
		{ value: remainingPicks, label: "Remaining", highlight: false },
	];

	return (
		<motion.div
			initial="hidden"
			animate="visible"
			variants={containerVariants}
			className="max-w-xl mx-auto"
		>
			{/* Card Container - Glassmorphism */}
			<motion.div
				variants={itemVariants}
				whileHover={{ scale: 1.01, y: -2 }}
				transition={{ type: "spring", stiffness: 400, damping: 30 }}
				className="bg-white/70 backdrop-blur-xl rounded-3xl border border-akgreener/10 shadow-xl shadow-akgreenest/[0.03] overflow-hidden"
			>
				{/* Header - Minimal frosted glass with subtle brand tint */}
				<motion.div
					variants={itemVariants}
					className="bg-gradient-to-br from-akgreener/5 to-white/70 backdrop-blur-sm px-6 py-6 border-b border-akgreener/10"
				>
					<div className="flex items-center justify-between">
						<div>
							<motion.div
								className="flex items-center gap-2 mb-3"
								initial={{ opacity: 0, x: -10 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 0.2, type: "spring", stiffness: 300 }}
							>
								<span className="px-3 py-1 bg-akgreener/10 backdrop-blur-sm rounded-full text-xs font-medium text-akgreenest border border-akgreener/20">
									Active
								</span>
								<span className="px-3 py-1 bg-gray-900/5 backdrop-blur-sm rounded-full text-xs font-medium text-gray-600 border border-gray-200/50">
									{isInstitutional ? "Institution" : "Individual"}
								</span>
							</motion.div>
							<h1 className="text-2xl font-semibold text-gray-800 capitalize tracking-tight">
								{subscription?.name} Plan
							</h1>
						</div>
						<motion.div
							className="text-right"
							initial={{ opacity: 0, x: 10 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.3, type: "spring", stiffness: 300 }}
						>
							<p className="text-gray-400 text-xs mb-1 uppercase tracking-wide">Expires in</p>
							<p className="text-lg font-medium text-akgreenest">
								<Moment to={subscription?.expiry_date}>{today}</Moment>
							</p>
						</motion.div>
					</div>
				</motion.div>

				{/* Stats Grid - Animated cards */}
				<motion.div variants={containerVariants} className="grid grid-cols-3 gap-1 p-2">
					{stats.map((stat, index) => (
						<motion.div
							key={stat.label}
							variants={statVariants}
							whileHover={{ scale: 1.03 }}
							whileTap={{ scale: 0.98 }}
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
							className={`px-4 py-5 text-center rounded-2xl cursor-default transition-colors duration-200 ${
								stat.highlight ? "hover:bg-akgreener/5" : "hover:bg-gray-50/80"
							}`}
						>
							<motion.p
								className={`text-2xl font-semibold tracking-tight ${
									stat.highlight ? "text-akgreener" : "text-gray-800"
								}`}
								initial={{ opacity: 0, scale: 0.5 }}
								animate={{ opacity: 1, scale: 1 }}
								transition={{ delay: 0.3 + index * 0.1, type: "spring", stiffness: 300 }}
							>
								{stat.value}
							</motion.p>
							<p
								className={`text-xs mt-1.5 font-medium uppercase tracking-wide ${
									stat.highlight ? "text-akgreener/60" : "text-gray-400"
								}`}
							>
								{stat.label}
							</p>
						</motion.div>
					))}
				</motion.div>

				{/* Progress Section */}
				<motion.div variants={itemVariants} className="px-6 py-5">
					<div className="flex items-center justify-between mb-3">
						<span className="text-sm font-medium text-gray-600">Usage</span>
						<motion.span
							className={`text-sm font-medium ${
								usagePercentage >= 90 ? "text-orangelib" : "text-akgreener"
							}`}
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.5 }}
						>
							{Math.round(usagePercentage)}%
						</motion.span>
					</div>
					<div className="w-full h-2 bg-akgreener/10 rounded-full overflow-hidden backdrop-blur-sm">
						<motion.div
							initial={{ width: 0, opacity: 0 }}
							animate={{
								width: `${usagePercentage}%`,
								opacity: 1,
							}}
							transition={{
								width: { type: "spring", stiffness: 100, damping: 20, delay: 0.4 },
								opacity: { duration: 0.3, delay: 0.3 },
							}}
							className={`h-full rounded-full ${
								usagePercentage >= 90
									? "bg-orangelib"
									: usagePercentage >= 70
										? "bg-akgreen"
										: "bg-akgreener"
							}`}
						/>
					</div>
					{usagePercentage >= 70 && (
						<motion.p
							initial={{ opacity: 0, y: 5 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ delay: 0.6 }}
							className={`text-xs mt-3 ${usagePercentage >= 90 ? "text-orangelib/70" : "text-gray-400"}`}
						>
							{usagePercentage >= 90
								? "Running low on picks. Consider upgrading."
								: "You've used most of your picks."}
						</motion.p>
					)}

					{/* Institutional Seats */}
					{isInstitutional && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							transition={{ delay: 0.5 }}
							className="flex items-center justify-between mt-5 pt-5 border-t border-akgreener/10"
						>
							<span className="text-sm text-gray-500">Subscription Seats</span>
							<span className="text-sm font-medium text-akgreenest">
								{subscription?.number_of_seats || 1} users
							</span>
						</motion.div>
					)}
				</motion.div>

				{/* Actions - Ghost buttons with micro-interactions */}
				<motion.div
					variants={itemVariants}
					className="px-6 py-5 bg-akgreener/[0.02] backdrop-blur-sm border-t border-akgreener/10"
				>
					<div className="flex gap-3">
						<motion.div
							className="flex-1"
							whileHover={{ scale: 1.02, y: -1 }}
							whileTap={{ scale: 0.98 }}
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
						>
							<Link
								to="/books"
								className="flex items-center justify-center gap-2 py-3 px-4 bg-akgreenest text-white text-sm font-medium rounded-xl transition-all duration-200 hover:bg-akgreenest/90 hover:shadow-lg hover:shadow-akgreenest/20"
							>
								<FiBook className="w-4 h-4" />
								Browse Books
							</Link>
						</motion.div>
						<motion.div
							className="flex-1"
							whileHover={{ scale: 1.02, y: -1 }}
							whileTap={{ scale: 0.98 }}
							transition={{ type: "spring", stiffness: 400, damping: 25 }}
						>
							<Link
								to="/my-library"
								className="flex items-center justify-center gap-2 py-3 px-4 bg-white/80 backdrop-blur-sm text-akgreenest text-sm font-medium rounded-xl border border-akgreener/20 transition-all duration-200 hover:bg-white hover:border-akgreener/40 hover:shadow-md hover:shadow-akgreener/10"
							>
								<FiGrid className="w-4 h-4" />
								My Library
							</Link>
						</motion.div>
					</div>
				</motion.div>
			</motion.div>

			{/* View Other Plans - Minimal link with hover animation */}
			<motion.div variants={itemVariants} className="text-center mt-8">
				<motion.button
					onClick={onViewPlans}
					whileHover={{ scale: 1.02 }}
					whileTap={{ scale: 0.98 }}
					className="group text-sm text-gray-400 hover:text-akgreener transition-colors duration-200 flex items-center gap-1.5 mx-auto"
				>
					<span className="relative">
						View other plans
						<span className="absolute bottom-0 left-0 w-0 h-px bg-akgreener group-hover:w-full transition-all duration-300" />
					</span>
					<motion.span
						className="inline-block"
						initial={{ x: 0 }}
						whileHover={{ x: 3 }}
						transition={{ type: "spring", stiffness: 400 }}
					>
						→
					</motion.span>
				</motion.button>
			</motion.div>
		</motion.div>
	);
};

// Empty Plans View Component
const EmptyPlansView = ({ canSeeInstitutionPlans, showBackButton, onBack }) => (
	<motion.div
		initial="hidden"
		animate="visible"
		variants={containerVariants}
		className="max-w-md mx-auto"
	>
		<motion.div
			variants={itemVariants}
			className="bg-white/70 backdrop-blur-xl rounded-3xl border border-akgreener/10 shadow-xl shadow-akgreenest/[0.03] overflow-hidden p-12 text-center"
		>
			{showBackButton && (
				<button
					onClick={onBack}
					className="inline-flex items-center gap-2 text-akgreener hover:text-akgreenest font-medium mb-6 transition-colors"
				>
					<FiChevronUp className="w-4 h-4" />
					Back to My Subscription
				</button>
			)}
			<div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-akgreen/10 to-akgreener/10 rounded-full flex items-center justify-center">
				<FiCreditCard className="w-12 h-12 text-akgreener/60" />
			</div>
			<h2 className="text-2xl font-bold text-gray-800 mb-3">No plans available</h2>
			<p className="text-gray-600 mb-8">
				{canSeeInstitutionPlans
					? "No institution plans are available at the moment."
					: "No individual plans are available at the moment."}
			</p>
			<Link
				to="/books"
				className="inline-flex items-center gap-2 py-3 px-6 bg-akgreenest text-white text-sm font-medium rounded-xl transition-all duration-200 hover:bg-akgreenest/90 hover:shadow-lg hover:shadow-akgreenest/20"
			>
				<FiBook className="w-4 h-4" />
				Browse Books
			</Link>
		</motion.div>
	</motion.div>
);

// Subscription Plans Table Component
const SubscriptionPlansTable = ({
	subscriptions,
	isSubscribed,
	currency,
	showBackButton,
	onBack,
	canSeeInstitutionPlans = true,
}) => {
	return (
		<>
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="text-center mb-12"
			>
				{showBackButton && (
					<button
						onClick={onBack}
						className="inline-flex items-center gap-2 text-akgreener hover:text-akgreenest font-medium mb-6 transition-colors"
					>
						<FiChevronUp className="w-4 h-4" />
						Back to My Subscription
					</button>
				)}
				<h2 className="text-2xl font-bold text-gray-800 mb-4">Compare All Subscription Plans</h2>
				<p className="text-gray-600 max-w-2xl mx-auto">
					{canSeeInstitutionPlans
						? "Choose the perfect plan for your institution"
						: "Choose the perfect plan for your needs as an individual reader"}
				</p>
			</motion.div>

			{/* Unified Pricing Table */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.1 }}
				className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
			>
				<div className="overflow-x-auto">
					<table className="w-full">
						<thead>
							<tr>
								<th className="w-1/4 px-6 py-4 text-left text-sm font-medium text-gray-500 bg-gray-50">
									Features
								</th>
								{subscriptions?.data?.map((subscription) => {
									const hasActiveSubscription =
										Boolean(isSubscribed) && Boolean(isSubscribed?.data?.id === subscription?.id);
									const isIndividual = subscription.category?.toLowerCase() === "individual";

									return (
										<th
											key={subscription.id}
											className={`px-6 py-4 text-center text-sm font-medium text-white relative ${
												hasActiveSubscription
													? "bg-akgreenest"
													: isIndividual
														? "bg-akgreen"
														: "bg-akgreener"
											}`}
										>
											<div className="flex flex-col items-center">
												{/* Category Badge */}
												<div
													className={`text-xs px-2 py-1 rounded-full mb-2 ${
														isIndividual
															? "bg-yellow-100 text-yellow-800"
															: "bg-green-100 text-green-800"
													}`}
												>
													{isIndividual ? "Individual" : "Institution"}
												</div>

												<span className="text-lg font-bold capitalize">{subscription.name}</span>
												{subscription?.name.toLowerCase() === "monthly" && (
													<span className="text-xs opacity-90">(recommended)</span>
												)}
												{hasActiveSubscription && (
													<span className="text-xs bg-white text-akgreenest px-2 py-1 rounded-full mt-1">
														Current Plan
													</span>
												)}
											</div>
										</th>
									);
								})}
							</tr>
						</thead>
						<tbody>
							{/* Duration Row */}
							<tr className="bg-white">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">Duration</td>
								{subscriptions?.data?.map((subscription) => {
									const duration = `${subscription?.duration} ${subscription?.time_unit}`;

									return (
										<td
											key={subscription.id}
											className="px-6 py-4 text-center text-sm text-gray-600"
										>
											{duration}
										</td>
									);
								})}
							</tr>

							{/* Price Row */}
							<tr className="bg-gray-50">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">Price</td>
								{subscriptions?.data?.map((subscription) => (
									<td key={subscription.id} className="px-6 py-4 text-center">
										<div className="flex flex-col items-center space-y-1">
											<span className="text-2xl font-bold text-akgreenest">
												{getMoneyPrefix(currency)}
												{currency === activeCurrencies.USD.code
													? subscription?.cost
													: subscription?.zwl_price}
											</span>
											<span className="text-xs text-gray-500">
												or {subscription?.credits} stars
											</span>
										</div>
									</td>
								))}
							</tr>

							{/* Books Access Row */}
							<tr className="bg-white">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">Books Access</td>
								{subscriptions?.data?.map((subscription) => (
									<td key={subscription.id} className="px-6 py-4 text-center text-sm text-gray-600">
										{subscription?.max_number_of_books} books
									</td>
								))}
							</tr>

							{/* Unlimited Free Titles Row */}
							<tr className="bg-gray-50">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">
									Unlimited Free Titles
								</td>
								{subscriptions?.data?.map((subscription) => (
									<td key={subscription.id} className="px-6 py-4 text-center">
										<span className="text-green-600 text-xl">✓</span>
									</td>
								))}
							</tr>

							{/* Offline Reading Row */}
							<tr className="bg-white">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">Offline Reading</td>
								{subscriptions?.data?.map((subscription) => (
									<td key={subscription.id} className="px-6 py-4 text-center">
										<span className="text-green-600 text-xl">✓</span>
									</td>
								))}
							</tr>

							{/* Action Row */}
							<tr className="bg-white">
								<td className="px-6 py-4 text-sm font-medium text-gray-700">Action</td>
								{subscriptions?.data?.map((subscription) => {
									const hasActiveSubscription =
										Boolean(isSubscribed) && Boolean(isSubscribed?.data?.id === subscription?.id);
									return (
										<td key={subscription.id} className="px-6 py-4 text-center">
											{hasActiveSubscription ? (
												<span className="py-2 px-4 text-sm bg-akgreenest rounded-full text-white font-medium">
													Current Plan
												</span>
											) : (
												<Link
													to={`/subscription/pay/${subscription.id}`}
													className="py-2 px-4 rounded-full bg-akgreener text-white text-sm font-medium transition-all duration-300 inline-block hover:bg-akgreenest hover:shadow-lg"
												>
													{isSubscribed?.data ? "Switch Plan" : "Subscribe Now"}
												</Link>
											)}
										</td>
									);
								})}
							</tr>
						</tbody>
					</table>
				</div>
			</motion.div>
		</>
	);
};

const Subscriptions = () => {
	const { currency } = useCurrencyStore();
	const { hasRoles } = useAuthStore();
	const [showPlans, setShowPlans] = useState(false);

	const { data: subscriptions, isLoading: isLoadingSubscriptions } = useFetchSubscriptions();
	const { data: isSubscribed, isLoading: isLoadingSubscribed } = useCheckIfUserIsSubscribed();

	const canSeeInstitutionPlans = hasRoles([Role.Institution, Role.Educator]);
	const filteredSubscriptions = {
		...subscriptions,
		data:
			subscriptions?.data?.filter((sub) => {
				const category = sub?.category?.toLowerCase();
				return canSeeInstitutionPlans ? category === "institution" : category === "individual";
			}) ?? [],
	};

	const isLoading = isLoadingSubscribed || isLoadingSubscriptions;
	const hasActiveSubscription = Boolean(isSubscribed?.data);
	const hasNoPlans = !filteredSubscriptions?.data?.length;

	if (isLoading) {
		return <SubscriptionsPageLoader />;
	}

	return (
		<Container className="min-h-screen flex flex-col w-full bg-gradient-to-b from-white via-gray-50 to-white">
			<div className="relative overflow-hidden">
				{/* Decorative background elements */}
				<div className="absolute inset-0 bg-gradient-to-b from-akgreener/5 to-transparent" />
				<div className="absolute top-0 left-0 w-96 h-96 bg-akgreener/10 rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2" />
				<div className="absolute top-0 right-0 w-96 h-96 bg-akgreenest/10 rounded-full filter blur-3xl translate-x-1/2 -translate-y-1/2" />

				<div className="relative mt-16 mb-20">
					<PaddingX>
						<div className="max-w-7xl mx-auto">
							{/* Conditional Rendering: Show subscription details, empty state, or plans */}
							{hasActiveSubscription && !showPlans ? (
								<ActiveSubscriptionView
									subscription={isSubscribed?.data}
									onViewPlans={() => setShowPlans(true)}
								/>
							) : hasNoPlans ? (
								<EmptyPlansView
									canSeeInstitutionPlans={canSeeInstitutionPlans}
									showBackButton={hasActiveSubscription}
									onBack={() => setShowPlans(false)}
								/>
							) : (
								<SubscriptionPlansTable
									subscriptions={filteredSubscriptions}
									isSubscribed={isSubscribed}
									currency={currency}
									showBackButton={hasActiveSubscription}
									onBack={() => setShowPlans(false)}
									canSeeInstitutionPlans={canSeeInstitutionPlans}
								/>
							)}
						</div>
					</PaddingX>
				</div>
			</div>
		</Container>
	);
};

export default Subscriptions;
